import type { ButtonProps } from '@s4l/ds'
import { BottomSheet, Button, DropdownSelector, HamburgerButton } from '@s4l/ds'
import { IconBusSide } from '@s4l/icons/IconBusSide'
import { IconCarSide } from '@s4l/icons/IconCarSide'
import { IconPlanetakeOff } from '@s4l/icons/IconPlanetakeOff'
import { IconHiperlink } from '@s4l/icons/IconHiperlink'
import { IconStays } from '@s4l/icons/IconStays'
import { useS4LContext } from '@s4l/react-context'
import type { AncillaryTypes } from '@s4l/stayforlong/booking/AncillaryType'
import { ListOfAncillary } from '@s4l/stayforlong/booking/ListOfAncillary'
import type { THEMES, VARIANTS } from 'client/components/Header/constants'
import {
  FULL,
  FULL_WITHOUT_COUNTRY,
  LOGO_WITH_HELP,
  THEME_DEFAULT,
} from 'client/components/Header/constants'
import HeaderLogo from 'client/components/HeaderLogo'
import HeaderWithSlots from 'client/components/HeaderWithSlots'
import { getLoginData } from 'client/contexts/Login/selectors'
import IconHelp from 'client/icons/Help'
import React, { useRef } from 'react'
import { shallowEqual, useSelector } from 'react-redux'
import LoginButton from 'src/comps/LoginButton'
import { useDevice } from 'src/hooks/useDevice'
import { useKafkaEvent } from 'src/hooks/useKafkaEvent'
import { SECTIONS } from 'src/hooks/useKafkaEvent/constants'
import { useMarket } from 'src/hooks/useMarket'
import { BreakpointSizes } from 'src/types/device'
import { CountrySelector } from '../CountrySelector'
import styles from './styles.module.css'

type Theme = (typeof THEMES)[number]
type Variant = (typeof VARIANTS)[number]
export interface HeaderProps {
  backButton?: boolean
  hasLogin?: boolean
  pageId: string | number
  theme?: Theme
  variant?: Variant
  ancillariesJSON: ReturnType<ListOfAncillary['toJSON']>
}

const Header = ({
  hasLogin = true,
  backButton = false,
  pageId,
  theme = THEME_DEFAULT,
  variant = FULL,
  ancillariesJSON,
}: HeaderProps) => {
  const user = useSelector(getLoginData, shallowEqual)

  const { helpCenterUrl } = useMarket()
  const { activeBreakpoint } = useDevice()
  const { i18n } = useS4LContext()

  const [isOpen, setIsOpen] = React.useState(false)

  const { sendEvent: sendClickHelpCenter } = useKafkaEvent({
    page: `${pageId}`,
    eventName: 'click_help_center',
  })

  const isErrorPage = typeof pageId === 'number' && pageId >= 400
  const isDesktop = activeBreakpoint >= BreakpointSizes.L

  const showUserActions = (): boolean => {
    return variant !== LOGO_WITH_HELP
  }

  const showCountrySelector = (): boolean => {
    return variant !== FULL_WITHOUT_COUNTRY
  }

  const { sendEvent: sendAncillaryClickEvent } = useKafkaEvent({
    page: `${pageId}`,
  })

  const rootRef = useRef(null)
  const targetRef = useRef(null)

  const ancillaries = ListOfAncillary.fromJSONOrEmpty(ancillariesJSON)
  const ancillariesIcons: Record<AncillaryTypes, React.ReactElement> = {
    stay: <IconStays />,
    transfer: <IconBusSide />,
    car_rental: <IconCarSide />,
    guide: <IconBusSide />,
    skycop: <IconBusSide />,
    'flight-hotel': <IconPlanetakeOff />,
  }

  const variantButton: ButtonProps['variant'] = isDesktop ? 'outlined-rounded' : 'icon-rounded'

  const getAncillarySection = (type: AncillaryTypes) => {
    if (type === 'flight-hotel') {
      return SECTIONS.ANCILLARIES_FLIGHT_HOTEL
    }

    return SECTIONS[`ANCILLARIES_${type.toUpperCase()}` as keyof typeof SECTIONS]
  }

  const ancillariesOrdered = ancillaries.sortByType([
    'stay',
    'skycop',
    'flight-hotel',
    'transfer',
    'guide',
    'car_rental',
  ])

  return (
    <HeaderWithSlots
      theme={theme}
      left={
        <HeaderLogo backButton={backButton} pageId={pageId} isPrime={user?.subscriptionPrime}>
          <div ref={targetRef} className={styles.ancillariesContainer}>
            {ancillariesOrdered.map(ancillary => (
              <Button
                key={ancillary.ctaKey}
                as="a"
                rel="nofollow"
                target="_blank"
                variant={ancillary.type === 'stay' ? 'outlined-rounded' : 'text-rounded'}
                href={ancillary.link}
                size="small"
                onClick={() =>
                  sendAncillaryClickEvent(
                    {
                      section: getAncillarySection(ancillary.type),
                    },
                    { ancillaryOrigin: 'header' }
                  )
                }
              >
                {i18n.formatMessage({ id: ancillary.ctaKey ?? '' })}
              </Button>
            ))}
          </div>
        </HeaderLogo>
      }
      right={
        <div ref={rootRef} className={styles.rightSideWrapper}>
          {showUserActions() && showCountrySelector() && (
            <div className={styles.countrySelector}>
              <CountrySelector toHomepage={isErrorPage} />
            </div>
          )}

          <div className={styles.section}>
            <Button
              leadingIcon={<IconHelp />}
              variant={variantButton}
              size="small"
              as="a"
              onClick={() => sendClickHelpCenter({ section: SECTIONS.HEADER })}
              href={helpCenterUrl}
              target="_blank"
            >
              <span className="display-none desktop:display-block">
                {i18n.formatMessage({ id: 'global.help-center' })}
              </span>
            </Button>
          </div>

          {showUserActions() && hasLogin && <LoginButton />}
          <BottomSheet onOpenChange={setIsOpen} direction="bottom">
            <BottomSheet.Trigger asChild>
              <HamburgerButton className="desktop:display-none" isOpen={isOpen} />
            </BottomSheet.Trigger>

            <BottomSheet.Content
              className={styles.sidebar}
              contentPadding={false}
              title={i18n.formatMessage({ id: 'global.menu' })}
            >
              {showUserActions() && showCountrySelector() && (
                <CountrySelector variant="dropdown" toHomepage={isErrorPage} />
              )}
              {ancillariesOrdered.map(ancillary => (
                <DropdownSelector
                  leadingIcon={ancillariesIcons[ancillary.type]}
                  trailingIcon={<IconHiperlink />}
                  href={ancillary.link}
                  target="_blank"
                  key={ancillary.type}
                  onClick={() =>
                    sendAncillaryClickEvent(
                      {
                        section: getAncillarySection(ancillary.type),
                      },
                      { ancillaryOrigin: 'header' }
                    )
                  }
                >
                  {i18n.formatMessage({ id: ancillary.ctaKey ?? '' })}
                </DropdownSelector>
              ))}

              <DropdownSelector
                leadingIcon={<IconHelp />}
                trailingIcon={<IconHiperlink />}
                target="_blank"
                href={helpCenterUrl}
                key="help-center"
                onClick={() => sendClickHelpCenter({ section: SECTIONS.HOTEL_HEADER })}
              >
                {i18n.formatMessage({ id: 'global.help-center-full' })}
              </DropdownSelector>
            </BottomSheet.Content>
          </BottomSheet>
        </div>
      }
    />
  )
}

export default Header
